<script setup lang="ts">
import { type MustGateEntry, mustGateEntryTool } from '@zerohuman/shared'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhPanel,
    ZhSavePreview,
    ZhStatusPill,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { api, type Role, type TaskInputField, type TaskSuccessor, type ToolSlot } from '@/api'
import ChatConversation from '@/components/ChatConversation.vue'
import GateDecisionCard from '@/components/GateDecisionCard.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import ModelTierField from '@/components/ModelTierField.vue'
import TaskApprovalsList from '@/components/TaskApprovalsList.vue'
import TaskGatesEditor, { type TaskGate } from '@/components/TaskGatesEditor.vue'
import TaskInputsEditor from '@/components/TaskInputsEditor.vue'
import TaskSuccessorsEditor from '@/components/TaskSuccessorsEditor.vue'
import TaskToolSlotsEditor from '@/components/TaskToolSlotsEditor.vue'
import TaskToolsPicker from '@/components/TaskToolsPicker.vue'
import { CREATE_TASK_FALLBACK_LINES, useCreateTaskConversation } from '@/composables/useCreateTaskConversation'
import { useCreateTaskProposal } from '@/composables/useCreateTaskProposal'
import { focusById, useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { useFormFunnel } from '@/composables/useFormFunnel'
import { useSuccessorChoices } from '@/composables/useSuccessorChoices'
import { useUiMode } from '@/composables/useUiMode'
import { optionalAmount, requiredName, SLUG_TAKEN } from '@/lib/fieldRules'
import { buildTaskPreview } from '@/lib/formPreview'
import { memberLabel } from '@/lib/memberLabel'
import { pickedId } from '@/lib/memberPickers'
import { requiredGateRow } from '@/lib/requiredGates'
import { type Approval, approvalOf, withApproval } from '@/lib/taskApprovals'
import { assigneeRoleIssue, workspaceJsonIssue } from '@/lib/taskFieldRules'
import { assignableRoles } from '@/lib/taskRoles'
import { posthog, posthogEnabled } from '@/posthog'

const router = useRouter()
const route = useRoute()
/**
 * The form's funnel events (#1774), under the `task` flow: the description submitted, Advanced opened, the save, and
 * leaving unsaved. Reports never wait or fail the form.
 */
const funnel = useFormFunnel('task')
const { mode: uiMode } = useUiMode()
const advancedMode = computed(() => uiMode.value === 'advanced')

/**
 * #244: Create task opens in the account's mode, with no switch of its own. Advanced is the form below. Standard is a
 * conversation with the COO, opened when the page is; until it is open, and whenever it cannot work, the page shows
 * the form, with one line saying why (`fallbackLine`). The mode is only ever read here, never written or kept.
 */
const {
    state: talkState,
    fallback: talkFallback,
    conversation,
    entries: talkEntries,
    thinking,
    draft: talkDraft,
    canSend,
    error: talkError,
    restarting,
    busy: talkBusy,
    toolLog,
    waitingCalls,
    callDecided,
    decidingCallId,
    start: startTalking,
    send,
    startOver,
    decideCall,
} = useCreateTaskConversation()
const talking = computed(() => !advancedMode.value && talkState.value === 'open' && conversation.value !== null)
/**
 * The COO the conversation is with, labelled as everywhere else in the portal and as the Chat page's thread does
 * (#1815): the COO an enterprise is made with is unnamed and reads "COO" in the thread's head, its labels and the
 * avatar, not a blank.
 */
const talkMember = computed(() =>
    conversation.value ? { ...conversation.value.member, name: memberLabel(conversation.value.member) } : null,
)
const fallbackLine = computed(() =>
    !advancedMode.value && talkFallback.value ? CREATE_TASK_FALLBACK_LINES[talkFallback.value] : null,
)
/**
 * #245: the proposal the conversation leads to, followed in it: drafting, the plan's sign-off question (decided as
 * Gates decides it), then what became of it. Followed only while the conversation is on the page.
 */
const {
    stage: proposalStage,
    plan: proposalPlan,
    gate: proposalGate,
    createdTask,
    failedItems,
    stopReason,
    stopSummary,
    applyingFor,
    deciding: proposalDeciding,
    decided: proposalDecided,
    error: proposalError,
    decide: decideProposal,
} = useCreateTaskProposal(conversation, () => talking.value)
watch(advancedMode, advanced => {
    if (!advanced) void startTalking()
})
const roles = ref<Role[]>([])
const error = ref<string | null>(null)
const saving = ref(false)
const form = ref({
    name: '',
    slug: '',
    kind: 'llm',
    status: 'active',
    assigneeRole: '',
    goal: '',
    metric: '',
    guardrails: '',
    tools: '',
    spendUsd: '',
    /** Empty runs the default model. A directory id pins this task to that model. */
    model: '',
    workspace: '',
    skillMd: '',
})

/**
 * A task belongs to a role, never a team: the select offers every role of the company, and each run goes to a holder
 * of it, on whatever team they sit. Every team where a member holds the role runs the task. The edit form on the detail
 * screen offers the same list.
 */
const teamRoles = computed(() => assignableRoles(roles.value))

/** What the role picker holds. No role chosen is held as no value at all, so it reads its placeholder. */
const roleChoice = computed(() => form.value.assigneeRole || undefined)

function chooseRole(choice: unknown): void {
    form.value.assigneeRole = pickedId(choice)
}

/**
 * The rules creating a task already has, checked while typing (#1757), as the edit form checks them: a name; a role
 * whenever there is one to choose (the Role select's own `required`); a spend per run that is blank or 0 or more;
 * Workspace JSON that parses. Slug has no rule of its own: a taken one is the server's to say, and is shown there.
 * In this order, Save focuses the first one that is wrong.
 */
const fields = useFieldValidation(
    computed(() => ({
        name: form.value.name,
        assigneeRole: form.value.assigneeRole,
        spendUsd: form.value.spendUsd,
        slug: form.value.slug,
        workspace: form.value.workspace,
    })),
    {
        name: requiredName('task'),
        assigneeRole: value => assigneeRoleIssue(value, teamRoles.value.length),
        spendUsd: optionalAmount('Spend per run'),
        slug: () => null,
        workspace: workspaceJsonIssue,
    },
    { idPrefix: 'create-task' },
)
/** The fields drawn in the Advanced section, which is closed on every load and so must open to show one. */
const fieldsInAdvanced = new Set<string>(['slug', 'workspace'])
/**
 * Whether the shared layout's Advanced is open (`v-model:advanced-open`, #1756 CTO decision 13). The form opens it
 * itself to show a field it holds; the layout reports only the person's own opening, so that one is never counted as
 * theirs (#1774, #1756 CTO decision 16).
 */
const advancedOpen = ref(false)

/** Opens the Advanced section, when it is closed, so a field in it can show its error and take focus. */
async function revealAdvanced(): Promise<void> {
    advancedOpen.value = true
    // The layout takes the open state on one tick, and its Collapsible draws what it holds on the next.
    await nextTick()
    await nextTick()
}

/** Shows a field the person has to fix: Advanced opened when it holds it, then focus on the field. */
async function focusField(key: Parameters<typeof fields.fieldId>[0]): Promise<void> {
    if (fieldsInAdvanced.has(key)) await revealAdvanced()
    focusById(fields.fieldId(key))
}

/**
 * Save with a field that breaks its rule sends nothing: every error shows, and the first such field takes focus. True
 * when every field keeps its rule.
 */
async function checkFields(): Promise<boolean> {
    const invalid = fields.validateAll()
    if (invalid === null) return true
    await focusField(invalid)
    return false
}

const inputRows = ref<TaskInputField[]>([])
/** The tool slots the task declares; left out of the create when there are none. */
const toolSlotRows = ref<ToolSlot[]>([])
const gateRows = ref<TaskGate[]>([])
/**
 * Whether the user chose the gates here (#1751): an edit they made in the gates editor. Until then the request leaves
 * `gates` out, so the task gets the CEO gates a task created without choosing any does. Once chosen, the rows are
 * sent as they stand, an emptied list included.
 *
 * A row `requireToolGates` adds when the picker says a tool needs a gate is not a choice: sent on its own, that one row
 * would replace every default and leave the task's other outside tools ungated. The defaults already hold it (the
 * API gives a task created without gates each must-gate tool by name, and `os.ask_ceo` beside `os.write_plan`), as a
 * CEO `specific` gate where the row says `manual`; both wait for the CEO on every call.
 */
const gatesChosen = ref(false)
/**
 * The editor also echoes a list the form wrote itself, such as a row `requireToolGates` added, so only a list that
 * differs from the rows the form holds is the user's edit.
 */
function editGates(next: TaskGate[]): void {
    if (!sameGates(next, gateRows.value)) gatesChosen.value = true
    gateRows.value = next
}
function sameGates(left: TaskGate[], right: TaskGate[]): boolean {
    return (
        left.length === right.length &&
        left.every(
            (gate, index) =>
                gate.tool === right[index]?.tool &&
                gate.owner === right[index]?.owner &&
                gate.approval === right[index]?.approval,
        )
    )
}
/**
 * Tools the picker says must be gated. Their approvals row is fixed on Ask me first: the OS refuses a task that holds
 * one without a CEO gate on it.
 */
const mustGateTools = ref<string[]>([])
/** An entry narrowed to some calls (`{ tool, match }`) is given a gate narrowed the same way (`requiredGateRow`). */
function requireToolGates(entries: MustGateEntry[]): void {
    for (const entry of entries) {
        const tool = mustGateEntryTool(entry)
        if (!mustGateTools.value.includes(tool)) mustGateTools.value.push(tool)
        const row = requiredGateRow(gateRows.value, entry)
        if (row) gateRows.value.push(row)
    }
}
/** A change to the gates the user made outside the raw editor: the approvals list. */
function setGates(next: TaskGate[]): void {
    if (sameGates(next, gateRows.value)) return
    gatesChosen.value = true
    gateRows.value = next
}
/** One tool's approval, chosen in the list: it writes the same rows the raw Gates editor shows, and counts as a choice. */
function chooseApproval(tool: string, choice: Approval): void {
    setGates(withApproval(gateRows.value, tool, choice))
}

/**
 * What the collapsed Advanced section holds, and so the count its header shows: the fields a founder never needs to
 * see to create a task. Kind is one of them only in portal Advanced mode (#1749), the only mode that shows it.
 */
const advancedFields = computed(() => [
    'slug',
    ...(advancedMode.value ? ['kind'] : []),
    'inputs',
    'toolSlots',
    'gates',
    'workspace',
    'successors',
])
const successorRows = ref<TaskSuccessor[]>([])
/**
 * The form as its draft keeps it: a restored draft is laid over the blank form, so one kept before a field existed
 * still leaves that field its starting value.
 */
const draftedForm = computed({
    get: () => form.value,
    set: (saved: typeof form.value) => {
        form.value = { ...form.value, ...saved }
    },
})
/** The fields a prefill link fills in (#1758), and what it filled each with. */
type LinkFilled = Partial<Record<'goal' | 'assigneeRole', string>>
/**
 * #1763: what the last prefill link filled in, kept with the draft. A field that still holds it was the link's, not
 * the person's, so a later link replaces it: one teammate's first task never comes back beside the next one's.
 */
const linkFilled = ref<LinkFilled>({})
/**
 * #1763: what is typed or picked here, Advanced fields and editor rows included, is kept in this browser until the
 * task is created, so leaving (Cancel, another link, a reload, a closed tab) loses nothing. Nothing of it is sent
 * until Create task is confirmed. Whether the gates were chosen goes with the rows, so the request after a restore
 * is the one it would have been. The rows a must-gate tool added, and which tools those are, are kept too: the picker
 * only says so when a tool is added, not for tools already on the form. Errors, the preview and whether Advanced is
 * open are not kept: Advanced opens closed on every load (#1753).
 */
const draft = useFormDraft('create-task', {
    form: draftedForm,
    inputRows,
    gateRows,
    gatesChosen,
    mustGateTools,
    successorRows,
    linkFilled,
})
// A restored draft is not a change made on this page: its fields show no error until changed or left.
fields.reset()
/** The tasks and outcomes the successors editor picks from (#1754). */
const successorChoices = useSuccessorChoices()
const { tasks: successorTasks, outcomes: successorOutcomes } = successorChoices

onMounted(async () => {
    if (!advancedMode.value) void startTalking()
    void successorChoices.load()
    try {
        roles.value = await api.roles()
        dropChoicesNoLongerHeld()
    } catch {
        error.value = 'Could not load roles.'
    }
    prefillFromQuery()
    // What the link filled in is not the person's change: no field shows an error until they change or leave it.
    fields.reset()
})

/**
 * #1763: a restored role the company no longer has is emptied. Run once the list has loaded, before any link fills in.
 */
function dropChoicesNoLongerHeld(): void {
    if (form.value.assigneeRole && !teamRoles.value.some(role => role.slug === form.value.assigneeRole)) {
        form.value.assigneeRole = ''
    }
}

/**
 * #1758: Add a teammate hands a described job's first task here as `?goal=…&role=<role id>`. The goal is filled in,
 * and the role is chosen when the company holds it; the founder finishes the task and creates it. Nothing is created
 * or run from the link alone. A `team` the link still carries is not read: a task belongs to a role, and the teammate
 * runs it on the teams they sit on.
 *
 * #1763: a restored draft wins, so the link fills only a field the person left blank. A field that still holds what an
 * earlier link filled in was never the person's: this link takes its place, and one this link does not fill is emptied.
 */
function prefillFromQuery(): void {
    const given = (key: string) => {
        const value = route.query[key]
        return typeof value === 'string' ? value.trim() : ''
    }
    const goal = given('goal')
    const role = roles.value.find(row => row.id === given('role'))
    if (!goal && !role) return
    const earlier = linkFilled.value
    const stillLinked = (field: keyof LinkFilled) => Boolean(earlier[field]) && form.value[field] === earlier[field]
    const open = (field: keyof LinkFilled) => !form.value[field] || stillLinked(field)
    const filled: LinkFilled = {}
    if (goal && open('goal')) {
        form.value.goal = filled.goal = goal
    } else if (stillLinked('goal')) {
        form.value.goal = ''
    }
    if (role && open('assigneeRole') && teamRoles.value.some(row => row.slug === role.slug)) {
        form.value.assigneeRole = filled.assigneeRole = role.slug
    } else if (stillLinked('assigneeRole')) {
        form.value.assigneeRole = ''
    }
    linkFilled.value = filled
}

function parseJson<T>(raw: string, fallback: T): T {
    if (!raw.trim()) {
        return fallback
    }
    return JSON.parse(raw) as T
}

/**
 * The save preview (#1756, FM8): pressing Create task says in plain words what the task will do, and nothing is sent
 * until that is confirmed. Its lines are held while it is open; any change to the form hides it, so a preview that no
 * longer says what would be saved can never be confirmed.
 */
const preview = ref<string[] | null>(null)
const toolsPicker = ref<{ labelFor(name: string): string } | null>(null)

/** A tool as the founder reads it: the name its card shows, never a raw dotted or snake_case name. */
function plainToolName(tool: string): string {
    const label = toolsPicker.value?.labelFor(tool) ?? tool
    return /[._#*]/.test(label) ? label.replace(/[._#*]+/g, ' ').trim() : label
}

/** Validate first (CTO decision 19 on #1756): a field that breaks its rule shows its error and opens no preview. */
async function openPreview(): Promise<void> {
    if (!(await checkFields())) return
    const role = teamRoles.value.find(row => row.slug === form.value.assigneeRole)
    const tools = form.value.tools
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
    const askFirst = tools
        .filter(tool => approvalOf(gateRows.value, tool).choice === 'ceo')
        .map(tool => `using ${plainToolName(tool)}`)
    const spend = form.value.spendUsd === '' ? null : Number(form.value.spendUsd)
    preview.value = buildTaskPreview({
        roleTitle: role?.name ?? '',
        goal: form.value.goal,
        askFirst,
        spendUsd: spend !== null && Number.isFinite(spend) ? spend : null,
    })
}

watch(
    [form, inputRows, toolSlotRows, gateRows, successorRows],
    () => {
        preview.value = null
    },
    { deep: true },
)

async function confirmPreview(): Promise<void> {
    preview.value = null
    await create()
}

/** Opening the Advanced section is a funnel fact (#1774); closing it is not. */
function toggleAdvanced(open: boolean): void {
    if (open) {
        funnel.advancedOpened()
    }
}

/**
 * Why the create was refused, at the top of the page. Standard mode never shows a code (#1757); Advanced shows it, as
 * it always has. A refusal shown on its own field needs nothing more in Standard.
 */
function refusalMessage(code: string, onField: boolean): string | null {
    if (advancedMode.value && code) return `Could not create (${code}).`
    return onField ? null : 'This task was not created. What you entered is still here: try again.'
}

async function create(): Promise<void> {
    // Confirm checks again before it sends (CTO decision 19 on #1756).
    if (!(await checkFields())) return
    saving.value = true
    error.value = null
    // The description is submitted: a fact whether or not the save then goes through.
    funnel.described()
    try {
        const draftForm = form.value
        const task = await api.createTask({
            name: draftForm.name,
            slug: draftForm.slug || undefined,
            kind: draftForm.kind,
            status: draftForm.status,
            assigneeRole: draftForm.assigneeRole || null,
            goal: draftForm.goal,
            metric: { text: draftForm.metric },
            guardrails: draftForm.guardrails
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean),
            tools: draftForm.tools
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean),
            inputs: inputRows.value,
            ...(toolSlotRows.value.length ? { toolSlots: toolSlotRows.value } : {}),
            ...(gatesChosen.value ? { gates: gateRows.value } : {}),
            spend: {
                moneyUsdPerRun: draftForm.spendUsd === '' ? undefined : Number(draftForm.spendUsd),
                model: draftForm.model || undefined,
            },
            workspace: parseJson(draftForm.workspace, null),
            successors: successorRows.value,
            skillMd: draftForm.skillMd,
        })
        if (posthogEnabled) {
            posthog.capture('task_created', { task_kind: draftForm.kind })
            posthog.logger.info('task creation completed', {
                event: 'task_created',
                task_kind: draftForm.kind,
            })
        }
        // Created: the draft goes before the new task opens, so the next visit starts blank. A refusal keeps it.
        draft.clear()
        // Saved: reported before the view leaves, so leaving for the new task is never counted as abandoning it.
        funnel.completed()
        await router.push(`/tasks/${task.id}`)
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        // A slug only the server can say is taken: shown on Slug with its fix (#1757).
        const slugTaken = code === 'task_slug_taken'
        error.value = refusalMessage(code, slugTaken)
        if (slugTaken) {
            fields.refuse('slug', SLUG_TAKEN)
            await focusField('slug')
        }
    } finally {
        saving.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/tasks">Tasks</RouterLink>
		</p>
		<!-- Issue 244: one line names the account's mode, and links to where it is changed. The page has no switch of its own. -->
		<p class="text-muted-foreground text-sm" data-testid="create-task-mode">
			{{ advancedMode ? 'Advanced mode: Create task opens as the form.' : 'Standard mode: Create task opens as a conversation with your COO.' }}
			Change it in <RouterLink class="underline" to="/account">account settings</RouterLink>.
		</p>
		<p v-if="!talking && fallbackLine" class="text-muted-foreground text-sm" data-testid="create-task-fallback" role="status">
			{{ fallbackLine }}
			<template v-if="talkFallback === 'no_coo'">Give someone the role in <RouterLink class="underline" to="/members">Members</RouterLink>.</template>
		</p>
		<!--
			Issue 244: in Standard, the conversation with the COO, the same component the Chat page draws, in the kit's
			bounded-height form: the page scrolls with the document, so the thread scrolls inside its own box. The
			thread, the thinking state, the tool log, the cards for tool calls waiting on an answer and the composer are
			all ChatConversation's, as on the Chat page; this page only says what happens.
		-->
		<div v-if="talking && conversation" class="flex flex-col" data-testid="create-task-conversation">
			<ChatConversation
				v-model:draft="talkDraft"
				bounded
				:member="talkMember ?? conversation.member"
				role="COO"
				:entries="talkEntries"
				:thinking="thinking"
				:details-open="advancedMode"
				:tool-log="toolLog"
				:calls="waitingCalls"
				:call-decided="callDecided"
				:deciding="decidingCallId !== null"
				:can-send="canSend"
				:closed="conversation.status === 'closed'"
				@send="send"
				@decide="decideCall"
			>
				<template #avatar><MemberAvatar :member="talkMember ?? conversation.member" class="size-full" /></template>
				<template #actions>
					<Button type="button" size="xs" variant="outline" data-testid="create-task-start-over" :disabled="restarting || talkBusy" @click="startOver">
						Start over
					</Button>
				</template>
				<template #top>
					<p v-if="talkError" class="text-destructive px-4 pt-2 text-sm" role="alert">{{ talkError }}</p>
				</template>
				<!--
					Issue 245: where the proposal stands, at the end of the conversation: drafting, the plan's sign-off
					question on the gate card Gates and the run page draw (Approve, Request changes with its note, Reject),
					then what became of it, with what to do next. Only the kit's pieces draw it.
				-->
				<template #log-after>
					<p v-if="proposalError" class="text-destructive py-1 text-sm" data-testid="create-task-decision-error" role="alert">{{ proposalError }}</p>
					<div v-if="proposalStage === 'drafting'" class="flex flex-col gap-1 py-2" data-testid="create-task-drafting" role="status">
						<ZhStatusPill tone="running">Drafting</ZhStatusPill>
						<p class="text-muted-foreground text-sm">
							Drafting the task from your brief. This can take a few minutes, and you can leave this page and come back. When it is ready, the question to approve it appears here.
						</p>
					</div>
					<div v-else-if="proposalStage === 'applying'" class="flex flex-col gap-1 py-2" data-testid="create-task-applying" role="status">
						<template v-if="applyingFor === 'refused'">
							<ZhStatusPill tone="running">Updating</ZhStatusPill>
							<p class="text-muted-foreground text-sm">What was decided will show here in a moment.</p>
						</template>
						<template v-else-if="applyingFor === 'reject'">
							<ZhStatusPill tone="running">Recording</ZhStatusPill>
							<p class="text-muted-foreground text-sm">Rejected. Recording your decision.</p>
						</template>
						<template v-else-if="applyingFor === 'request_changes'">
							<ZhStatusPill tone="running">Recording</ZhStatusPill>
							<p class="text-muted-foreground text-sm">Sending your changes. The task will be drafted again with them.</p>
						</template>
						<template v-else>
							<ZhStatusPill tone="running">Creating</ZhStatusPill>
							<p class="text-muted-foreground text-sm">Approved. The task is being created and will show here when it is ready.</p>
						</template>
					</div>
					<div v-else-if="proposalStage === 'proposal' && proposalGate" class="flex flex-col gap-2 py-2" data-testid="create-task-proposal">
						<GateDecisionCard :gate="proposalGate" :busy="proposalDeciding" :decided="proposalDecided" @decide="decideProposal" />
					</div>
					<ZhPanel v-else-if="proposalStage === 'created'" class="flex flex-col gap-2" data-testid="create-task-created" role="status">
						<ZhStatusPill tone="completed">Created</ZhStatusPill>
						<p class="text-sm">
							<template v-if="createdTask"><strong>{{ createdTask.name }}</strong> is created.</template>
							<template v-else>The task is created.</template>
						</p>
						<div class="flex flex-wrap gap-2">
							<Button v-if="createdTask" as-child size="sm">
								<RouterLink :to="`/tasks/${createdTask.id}`">Open task</RouterLink>
							</Button>
							<Button type="button" size="sm" variant="outline" :disabled="restarting || talkBusy" @click="startOver">Create another</Button>
						</div>
					</ZhPanel>
					<ZhPanel v-else-if="proposalStage === 'handed_off'" class="flex flex-col gap-2" data-testid="create-task-handed-off" role="status">
						<ZhStatusPill tone="neutral">Approved</ZhStatusPill>
						<p class="text-sm">No task was created. This was approved, but it was not written as a new task, so there is nothing here for the OS to set up.</p>
						<div class="flex flex-wrap gap-2">
							<Button type="button" size="sm" variant="outline" :disabled="restarting || talkBusy" @click="startOver">Start over</Button>
							<Button v-if="proposalPlan" as-child size="sm" variant="outline">
								<RouterLink :to="`/plans/${proposalPlan.id}`">See what was approved</RouterLink>
							</Button>
						</div>
					</ZhPanel>
					<ZhPanel v-else-if="proposalStage === 'rejected'" class="flex flex-col gap-2" data-testid="create-task-rejected" role="status">
						<ZhStatusPill tone="neutral">Rejected</ZhStatusPill>
						<p class="text-sm">No task was created: the proposed task was rejected.</p>
						<div class="flex flex-wrap gap-2">
							<Button type="button" size="sm" variant="outline" :disabled="restarting || talkBusy" @click="startOver">Start over</Button>
						</div>
					</ZhPanel>
					<ZhPanel v-else-if="proposalStage === 'failed'" tone="failed" class="flex flex-col gap-2" data-testid="create-task-failed" role="alert">
						<ZhStatusPill tone="failed">Not created</ZhStatusPill>
						<p class="text-sm">No task was created. It was approved, but it could not be saved.</p>
						<ul v-if="failedItems.length" class="flex flex-col gap-1 text-sm">
							<li v-for="item in failedItems" :key="item.handle">
								<strong>{{ item.label }}</strong> could not be saved.
								<details>
									<summary class="text-muted-foreground cursor-pointer">Details</summary>
									<p class="text-muted-foreground">{{ item.reason }}</p>
								</details>
							</li>
						</ul>
						<div class="flex flex-wrap gap-2">
							<Button type="button" size="sm" variant="outline" :disabled="restarting || talkBusy" @click="startOver">Start over</Button>
							<Button v-if="proposalPlan" as-child size="sm" variant="outline">
								<RouterLink :to="`/plans/${proposalPlan.id}`">See each part and what went wrong</RouterLink>
							</Button>
						</div>
					</ZhPanel>
					<ZhPanel v-else-if="proposalStage === 'stopped'" tone="failed" class="flex flex-col gap-2" data-testid="create-task-stopped" role="alert">
						<ZhStatusPill tone="failed">Stopped</ZhStatusPill>
						<p class="text-sm">{{ stopSummary }}</p>
						<details v-if="stopReason" class="text-sm">
							<summary class="text-muted-foreground cursor-pointer">Details</summary>
							<p class="text-muted-foreground">{{ stopReason }}</p>
						</details>
						<div class="flex flex-wrap gap-2">
							<Button type="button" size="sm" variant="outline" :disabled="restarting || talkBusy" @click="startOver">Start over</Button>
						</div>
					</ZhPanel>
				</template>
			</ChatConversation>
		</div>
		<!--
			#1756: the shared form shell (title, one-sentence purpose, fields, Advanced, actions bottom-right), one help
			line on every field, and a plain-words preview before anything is saved. Every control is the kit's (#1227).
		-->
		<ZhFormLayout
			v-else
			v-model:advanced-open="advancedOpen"
			title="Create task"
			purpose="Creates the task and its first version, which you can edit and save again later."
			:advanced-count="advancedFields.length"
			data-testid="create-form"
			@submit.prevent="openPreview"
			@advanced-toggle="toggleAdvanced"
		>
			<template #status>
				<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
			</template>
			<div class="grid gap-3 sm:grid-cols-2">
				<!-- #1757's checks (CTO decisions 18 and 20 on #1756): a fixed id, described by the error, then the help line. -->
				<ZhFormField :id="fields.fieldId('name')" :error-id="fields.message('name').id" label="Name" help="What you call this task." example="Qualify new leads">
					<template #default="{ controlId, describedBy }">
						<Input v-model="form.name" required v-bind="fields.bind('name')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('name')" />
					</template>
				</ZhFormField>
				<ZhFormField v-slot="{ controlId, helpId }" label="Status" label-id="create-task-status" help="Active tasks can run; deprecated ones are kept for the record." example="active">
					<Select v-model="form.status">
						<SelectTrigger :id="controlId" class="w-full" aria-labelledby="create-task-status" :aria-describedby="helpId">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="active">active</SelectItem>
							<SelectItem value="deprecated">deprecated</SelectItem>
						</SelectContent>
					</Select>
				</ZhFormField>
				<ZhFormField
					:id="fields.fieldId('assigneeRole')"
					:error-id="fields.message('assigneeRole').id"
					label="Role"
					label-id="create-task-role"
					help="Each run goes to a holder of this role, whatever team they sit on."
					example="Marketer"
				>
					<template #default="{ controlId, describedBy }">
						<!-- Named, so the form carries Reka's own native select and the browser still refuses an empty role. -->
						<Select
							:model-value="roleChoice"
							name="assigneeRole"
							required
							data-testid="task-assignee-role-select"
							@update:model-value="chooseRole"
						>
							<SelectTrigger
								class="w-full"
								aria-labelledby="create-task-role"
								v-bind="fields.bind('assigneeRole')"
								:id="controlId"
								:aria-describedby="describedBy"
							>
								<SelectValue :placeholder="teamRoles.length > 0 ? 'Choose a role' : 'Create a role first'" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="role in teamRoles" :key="role.slug" :value="role.slug">{{ role.name }}</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('assigneeRole')" />
					</template>
				</ZhFormField>
				<ZhFormField :id="fields.fieldId('spendUsd')" :error-id="fields.message('spendUsd').id" label="Spend USD / run" help="The most one run may spend in dollars; leave it empty for no cap." example="0.50">
					<template #default="{ controlId, describedBy }">
						<Input v-model="form.spendUsd" type="number" step="0.01" v-bind="fields.bind('spendUsd')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('spendUsd')" />
					</template>
				</ZhFormField>
				<ZhFormField label="Model" help="The AI model that runs it; Recommended suits most tasks." example="Recommended" group>
					<ModelTierField :form="form" bare />
				</ZhFormField>
			</div>
			<ZhFormField v-slot="{ controlId, helpId }" label="Goal" help="What one run should get done, in a few words." example="draft a post">
				<Textarea :id="controlId" v-model="form.goal" rows="2" :aria-describedby="helpId" />
			</ZhFormField>
			<ZhFormField v-slot="{ controlId, helpId }" label="Metrics, one per line" help="How you will know it worked." example="Posts published each week">
				<Textarea :id="controlId" v-model="form.metric" rows="3" :aria-describedby="helpId" />
			</ZhFormField>
			<ZhFormField v-slot="{ controlId, helpId }" label="Guardrails (one per line)" help="Rules every run must keep." example="Never post without a link">
				<Textarea :id="controlId" v-model="form.guardrails" rows="4" :aria-describedby="helpId" />
			</ZhFormField>
			<ZhFormField label="Tools and approvals" help="What it can use, and whether each tool waits for you first." example="Slack, set to Ask me first" group>
			<TaskToolsPicker ref="toolsPicker" @require-gates="requireToolGates" v-model="form.tools" :role-id="teamRoles.find(role => role.slug === form.assigneeRole)?.id">
				<!-- #1753: each tool on the task, set to Ask me first, Let it run or Ask the lead, named as its card names it. -->
				<template #selected="{ tools, label }">
					<TaskApprovalsList
						v-if="tools.length > 0"
						:tools="tools"
						:label="label"
						:gates="gateRows"
						:must-gate="mustGateTools"
						:show-tool-names="advancedMode"
						@choose="chooseApproval"
					/>
				</template>
			</TaskToolsPicker>
			</ZhFormField>
			<ZhFormField v-slot="{ controlId, helpId }" label="SKILL.md" help="Extra instructions the agent reads before every run." example="Write in a friendly, short tone">
				<Textarea :id="controlId" v-model="form.skillMd" rows="10" :aria-describedby="helpId" />
			</ZhFormField>
			<!--
				#1753: identifiers and raw formats sit in Advanced, closed on every load and never remembered. Its header
				counts what it holds. What is typed here is the form's, so closing it loses nothing and the request is the same.
			-->
			<template #advanced>
				<div class="grid gap-3 sm:grid-cols-2">
					<ZhFormField :id="fields.fieldId('slug')" :error-id="fields.message('slug').id" label="Slug" help="The short name used in links, made from the name if empty." example="qualify-leads">
						<template #default="{ controlId, describedBy }">
							<Input v-model="form.slug" placeholder="from the name if empty" v-bind="fields.bind('slug')" :id="controlId" :aria-describedby="describedBy" />
						</template>
						<template #error>
							<ZhFieldError v-bind="fields.message('slug')" />
						</template>
					</ZhFormField>
					<!-- #1749: Kind is Advanced only. Standard never shows it, and a task made there keeps the default kind. -->
					<ZhFormField v-if="advancedMode" v-slot="{ controlId, helpId }" label="Kind" label-id="create-task-kind" help="Whether an AI agent runs it or fixed steps do." example="llm">
						<Select v-model="form.kind">
							<SelectTrigger :id="controlId" class="w-full" aria-labelledby="create-task-kind" :aria-describedby="helpId">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="llm">llm</SelectItem>
								<SelectItem value="deterministic">deterministic</SelectItem>
							</SelectContent>
						</Select>
					</ZhFormField>
				</div>
				<ZhFormField label="Inputs" help="Values someone gives each time it runs." example="the customer's email address" group>
					<TaskInputsEditor v-model="inputRows" data-testid="task-inputs-editor" />
				</ZhFormField>
				<ZhFormField label="Tool slots" help="Tools named by a slot each company fills with its own connection." example="codehost" group>
					<TaskToolSlotsEditor v-model="toolSlotRows" />
				</ZhFormField>
				<ZhFormField label="Gates" help="The approval rules themselves, as the approvals list writes them." example="the CEO approves every post" group>
					<TaskGatesEditor :model-value="gateRows" data-testid="task-gates-editor" @update:model-value="editGates" />
				</ZhFormField>
				<ZhFormField :id="fields.fieldId('workspace')" :error-id="fields.message('workspace').id" label="Workspace JSON" help="Where its runs work, such as a code repository." example='{"repo": "acme/site"}'>
					<template #default="{ controlId, describedBy }">
						<Textarea v-model="form.workspace" rows="4" v-bind="fields.bind('workspace')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('workspace')" />
					</template>
				</ZhFormField>
				<ZhFormField label="Successors" help="Tasks that start when this one finishes with a given outcome." example="Publish post after a draft is approved" group>
					<TaskSuccessorsEditor
						v-model="successorRows"
						:tasks="successorTasks"
						:outcomes="successorOutcomes"
						data-testid="task-successors-editor"
					/>
				</ZhFormField>
			</template>
			<template #cancel>
				<Button type="button" size="sm" variant="outline" @click="router.push('/tasks')">Cancel</Button>
			</template>
			<template #primary>
				<Button type="submit" size="sm" :disabled="saving">Create task</Button>
			</template>
			<template #preview>
				<ZhSavePreview v-if="preview" :lines="preview" confirm-label="Create task" :pending="saving" @confirm="confirmPreview" @edit="preview = null" />
			</template>
		</ZhFormLayout>
	</section>
</template>
